import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ShieldCheck,
  Truck,
  Map as MapIcon,
  Handshake,
  Boxes,
  GraduationCap,
  Headphones,
  Network,
  BadgeCheck,
} from "lucide-react";

const WAREHOUSE_IMG =
  "https://customer-assets.emergentagent.com/job_agrim-pet-resin/artifacts/5z691o6k_image.png";
const POLYMER_IMG =
  "https://images.unsplash.com/photo-1615565016433-69797a5e3e11?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function Eyebrow({ children }) {
  return (
    <div className="flex items-center gap-2 font-mono-plex text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-500">
      <span className="h-[6px] w-[6px] bg-orange-500" />
      {children}
    </div>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ---------- HERO (Bento) ---------- */}
      <section
        data-testid="home-hero"
        className="border-b border-zinc-200"
      >
        <div className="mx-auto max-w-[1400px] px-6 pt-16 pb-10 md:px-12 md:pt-24 md:pb-16 lg:px-16">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-8">
              <h1 className="font-heading text-[42px] font-black leading-[0.95] tracking-tighter text-zinc-950 sm:text-6xl lg:text-[88px]">
                Sourcing{" "}
                <span className="italic font-light">Solutions.</span>
                <br />
                Powering{" "}
                <span className="relative inline-block">
                  Progress.
                  <span className="absolute -bottom-1 left-0 h-[6px] w-full bg-orange-500" />
                </span>
              </h1>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-600 md:text-lg">
                A trusted name in PET resin trading, distribution, and workforce
                development across Western and Central India — engineered for
                manufacturers who power the beverage, bottling, and packaging
                industries.
              </p>
            </div>

            <div className="md:col-span-4">
              <div className="grid h-full grid-cols-2 gap-4">
                <div className="col-span-2 h-52 overflow-hidden border border-zinc-200 sm:h-60 md:h-56">
                  <img
                    src={WAREHOUSE_IMG}
                    alt="Jumbo bags of PET resin stacked in warehouse"
                    className="h-full w-full object-cover transition duration-700 hover:scale-[1.02]"
                  />
                </div>
                <div className="h-44 overflow-hidden border border-zinc-200 bg-zinc-100">
                  <img
                    src={POLYMER_IMG}
                    alt="PET resin granules close-up"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex h-44 flex-col justify-between border border-zinc-200 bg-zinc-950 p-5 text-white">
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.24em] text-zinc-400">
                    Core Competency
                  </div>
                  <div>
                    <div className="font-heading text-2xl font-black leading-tight">
                      Customer
                      <br />
                      First
                    </div>
                  </div>
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-orange-400">
                    Service · Relationships
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- WHO WE ARE ---------- */}
      <section
        data-testid="home-who-we-are"
        className="border-b border-zinc-200"
      >
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 py-20 md:grid-cols-12 md:px-12 md:py-32 lg:px-16">
          <div className="md:col-span-4">
            <Eyebrow>01 · Who We Are</Eyebrow>
            <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
              A growing force in India's polymer trade.
            </h2>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <p className="text-lg leading-relaxed text-zinc-700">
              Agrim Ventures Private Limited is a growing force in India's
              polymer trade industry, specializing in the import and trading of
              PET resin and serving as an authorized distributor for{" "}
              <span className="text-zinc-950">Chiripal Polyfilms Ltd</span>.
              With a strong and wide distribution network, we deliver quality
              materials to manufacturers who power the beverage, bottling, and
              packaging industries.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-zinc-700">
              Beyond business, we believe in building futures — our CSR arm
              drives meaningful skilling initiatives that empower India's
              workforce.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- CORE STRENGTHS (Technical grid) ---------- */}
      <section
        data-testid="home-core-strengths"
        className="border-b border-zinc-200"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32 lg:px-16">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Eyebrow>02 · Core Strengths</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
                Three pillars. One mission.
              </h2>
            </div>
            <div className="max-w-sm text-zinc-600">
              Every vertical is built on quality sourcing, regional
              relationships, and measurable outcomes.
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 border-l border-t border-zinc-200 md:grid-cols-3">
            {[
              {
                no: "01",
                title: "PET Resin Trading & Import",
                desc: "Reliable sourcing and supply of bottle-grade PET resin for beverage, water, food, and pharmaceutical bottling applications.",
              },
              {
                no: "02",
                title: "Authorized Distributor",
                desc: "Proud distribution partner of Chiripal Polyfilms Ltd, delivering premium polymer products through a wide distribution network.",
              },
              {
                no: "03",
                title: "CSR & Skilling",
                desc: "Empowering youth and building employable talent through in-house and outsourced skilling programs.",
              },
            ].map((c) => (
              <div
                key={c.no}
                data-testid={`core-card-${c.no}`}
                className="group relative flex min-h-[280px] flex-col justify-between border-b border-r border-zinc-200 bg-white p-8 transition-colors hover:bg-zinc-50"
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono-plex text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400">
                    {c.no}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-zinc-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-zinc-950" />
                </div>
                <div className="mt-16">
                  <div className="font-heading text-2xl font-bold leading-tight text-zinc-950">
                    {c.title}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-zinc-600">
                    {c.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- CUSTOMER RELATIONSHIP (Core Competency) ---------- */}
      <section
        data-testid="home-distribution"
        className="overflow-hidden border-b border-zinc-200 bg-zinc-50"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-28 lg:px-16">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <Eyebrow>03 · Our Core Competency</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
                Customer service, long-term relationships.
              </h2>
              <p className="mt-6 max-w-md text-zinc-600">
                At the heart of everything we do is a simple belief — business
                runs on trust. We invest in understanding each customer's
                needs, respond with speed, and show up consistently over the
                long term.
              </p>
            </div>

            <div className="md:col-span-7">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  {
                    k: "01",
                    icon: Headphones,
                    title: "Responsive Support",
                    desc: "Quick turnarounds on inquiries, pricing, and technical queries — we stay reachable.",
                  },
                  {
                    k: "02",
                    icon: Handshake,
                    title: "Relationship-First Approach",
                    desc: "We build partnerships for the long haul, not one-off transactions.",
                  },
                  {
                    k: "03",
                    icon: Network,
                    title: "Wide Distribution Network",
                    desc: "A strong, well-connected supply network that keeps deliveries timely and consistent.",
                  },
                  {
                    k: "04",
                    icon: BadgeCheck,
                    title: "Trusted by Manufacturers",
                    desc: "Reputation built on reliability, transparency, and honest business dealings.",
                  },
                ].map((c) => (
                  <div
                    key={c.k}
                    data-testid={`competency-card-${c.k}`}
                    className="group flex flex-col border border-zinc-300 bg-white p-6 transition-colors hover:border-zinc-950"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex h-11 w-11 items-center justify-center border border-zinc-950 bg-white text-zinc-950 transition-colors group-hover:bg-zinc-950 group-hover:text-white">
                        <c.icon className="h-4 w-4" strokeWidth={1.5} />
                      </div>
                      <span className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                        {c.k}
                      </span>
                    </div>
                    <div className="mt-8">
                      <div className="font-heading text-lg font-bold leading-tight text-zinc-950">
                        {c.title}
                      </div>
                      <p className="mt-3 text-sm leading-relaxed text-zinc-600">
                        {c.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Marquee ribbon */}
        <div className="relative border-y border-zinc-200 bg-white py-6">
          <div className="flex whitespace-nowrap animate-marquee">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center gap-14 pr-14">
                {[
                  "Customer First",
                  "Trusted Partnerships",
                  "PET Resin",
                  "Chiripal Polyfilms",
                  "CSR Skilling",
                  "Packaging",
                  "Bottling",
                  "Beverage",
                  "Wide Distribution",
                ].map((t) => (
                  <span
                    key={`${i}-${t}`}
                    className="font-heading text-3xl font-bold tracking-tight text-zinc-950 md:text-5xl"
                  >
                    {t}
                    <span className="mx-6 inline-block align-middle text-orange-500">
                      ✦
                    </span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- WHY CHOOSE US ---------- */}
      <section
        data-testid="home-why-choose-us"
        className="border-b border-zinc-200"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32 lg:px-16">
          <Eyebrow>04 · Why Choose Us</Eyebrow>
          <h2 className="mt-6 max-w-3xl font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
            Built on four non-negotiables.
          </h2>

          <div className="mt-14 grid grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Handshake,
                title: "Customer First",
                desc: "Our core competency — responsive service and long-term relationships that manufacturers can count on.",
              },
              {
                icon: ShieldCheck,
                title: "Quality First",
                desc: "Consistent material standards and trusted sourcing across every consignment.",
              },
              {
                icon: Truck,
                title: "Wide Distribution",
                desc: "A strong, well-connected network ensuring timely, reliable supply for manufacturing schedules.",
              },
              {
                icon: MapIcon,
                title: "Responsible Business",
                desc: "CSR-led initiatives that create real, measurable social impact beyond profits.",
              },
            ].map((item, i) => (
              <div
                key={item.title}
                className="group flex flex-col gap-6 bg-white p-8 transition-colors hover:bg-zinc-50"
                data-testid={`why-card-${i + 1}`}
              >
                <div className="flex h-12 w-12 items-center justify-center border border-zinc-950 bg-white text-zinc-950 transition-colors group-hover:bg-zinc-950 group-hover:text-white">
                  <item.icon className="h-5 w-5" strokeWidth={1.4} />
                </div>
                <div>
                  <div className="font-heading text-xl font-bold text-zinc-950">
                    {item.title}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-600">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- CLOSING CTA ---------- */}
      <section data-testid="home-closing-cta" className="bg-zinc-950 text-white">
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32 lg:px-16">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:items-end">
            <div className="md:col-span-8">
              <div className="flex items-center gap-2 font-mono-plex text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400">
                <span className="h-[6px] w-[6px] bg-orange-500" />
                Let's build together
              </div>
              <h2 className="mt-6 font-heading text-4xl font-black leading-[1.02] tracking-tighter md:text-7xl">
                Let's build something{" "}
                <span className="italic font-light">meaningful</span> together.
              </h2>
              <p className="mt-8 max-w-2xl text-lg text-zinc-400">
                Whether you're sourcing PET resin, exploring distribution
                partnerships, or collaborating on CSR skilling — we'd love to
                hear from you.
              </p>
            </div>
            <div className="md:col-span-4">
              <Link
                to="/contact"
                data-testid="closing-cta-contact"
                className="group flex items-center justify-between border border-white/30 bg-transparent px-6 py-5 font-mono-plex text-[12px] font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:bg-white hover:text-zinc-950"
              >
                Get in Touch
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
